import React from 'react';
import { Loader2, Send, Trash2, X } from 'lucide-react';

/**
 * ComposeSurface — the full-page compose chrome, shared by both lanes.
 *
 * ## Why this exists
 *
 * `ComposePage` (departmental) and `PersonalComposePage` (personal) each drew
 * their own header and footer. They had drifted into two different pages: one
 * led with "← Back / New Message" and buried Send bottom-right behind Discard
 * and Cancel, the other led with a violet Send; the footers disagreed about
 * where the sending identity is stated. Neither matched the design, which draws
 * ONE compose surface for both.
 *
 * The chrome is the part that was duplicated, so the chrome is what moved here.
 * `MailComposer` still owns the middle (identity, recipients, subject, editor);
 * the pages still own the send path, the draft and the navigation, because
 * those genuinely differ per lane.
 *
 * ## The layout contract
 *
 * The design draws compose as a full-bleed panel filling the content column —
 * no max-width, no page gutter, hairline rows edge to edge, and an action bar
 * and footer that stay put while the middle scrolls. So:
 *
 *   - the root is a bounded flex column (`flex-1 min-h-0`), which requires the
 *     route to be on `RuntimeShell`'s `isFullscreenPage` list. Off that list the
 *     main column is a padded page scrollport, `h-full` resolves to `auto`, and
 *     the panel grows to its content height inside a gutter — which is exactly
 *     the "not full width, wrong layout" this replaces;
 *   - the middle is the ONLY scroll region;
 *   - `flex-shrink-0` on the bars is load-bearing: without it a long recipient
 *     list squeezes the footer's height instead of scrolling the middle.
 *
 * ## The Send caret is deliberately absent
 *
 * The design draws Send as a split control with a chevron. Scheduled send does
 * not exist anywhere in this product — no `send_at`, no `sendLater`, no
 * `scheduled_send` — and #623 already ruled that Send ships as a plain button
 * rather than a split control wrapped around a single item. A chevron that
 * opens nothing is a worse lie than a missing chevron, so the button keeps the
 * design's exact size, colour and radius without the second segment. Build
 * scheduled send and the caret follows it.
 */
export default function ComposeSurface({
    testId,
    children,

    // ── Send ────────────────────────────────────────────────────────────
    onSend,
    sending = false,
    sendDisabled = false,
    /** #970 — false while the body holds no typed text (only a signature):
     *  Send is off, without the busy spinner, with a hint. */
    hasContent = true,
    sendLabel = 'Send',
    /** The historical id, on the action-bar button — the primary Send. */
    sendTestId,
    /** The footer's Send. A second id so neither assertion is ambiguous. */
    footerSendTestId,

    // ── Discard / close ─────────────────────────────────────────────────
    /** Clears the draft and the fields. Both the action-bar trash (which the
     *  design titles "Discard") and the footer's labelled button call it. */
    onDiscard,
    discardTestId,
    /** Leaves the page. The design's X is the only way back — there is no
     *  separate Back button — so this carries the navigation the old header's
     *  "← Back" and "Cancel" both used to. */
    onClose,

    // ── Footer ──────────────────────────────────────────────────────────
    /** "Sending as … · via Gmail API" — the design centres it. */
    note,
    noteTestId,
    /** Lane-specific footer controls (e.g. the personal lane's Track toggle). */
    footerExtra,
}) {
    const busy = sending || sendDisabled;
    const blocked = busy || !hasContent;
    const sendTitle = !busy && !hasContent ? 'Type a message first' : undefined;

    return (
        <div className="flex flex-col flex-1 min-h-0 bg-white" data-testid={testId}>
            {/* Action bar */}
            <div className="flex-shrink-0 flex items-center gap-3 px-5 py-[10px] border-b border-[#EDEBE6]">
                <button
                    type="button"
                    onClick={onSend}
                    disabled={blocked}
                    title={sendTitle}
                    data-testid={sendTestId}
                    className="flex items-center gap-2 h-9 px-4 rounded-[9px] bg-[#4D11F6]
                               hover:bg-[#3F0BD1] disabled:opacity-60 text-white
                               text-[13.5px] font-semibold transition-colors"
                >
                    {busy
                        ? <Loader2 className="w-[15px] h-[15px] animate-spin" />
                        : <Send className="w-[15px] h-[15px]" />}
                    {sendLabel}
                </button>

                <div className="flex-1" />

                <button
                    type="button"
                    title="Discard"
                    aria-label="Discard"
                    onClick={onDiscard}
                    className="flex items-center justify-center w-[34px] h-[34px] rounded-[9px]
                               text-[#57504B] hover:bg-[#F4F2EE] transition-colors"
                    data-testid="compose-discard-icon"
                >
                    <Trash2 className="w-[17px] h-[17px]" />
                </button>
                <button
                    type="button"
                    title="Close"
                    aria-label="Close"
                    onClick={onClose}
                    className="flex items-center justify-center w-[34px] h-[34px] rounded-[9px]
                               text-[#57504B] hover:bg-[#F4F2EE] transition-colors"
                    data-testid="compose-close"
                >
                    <X className="w-[17px] h-[17px]" />
                </button>
            </div>

            {/* The one scroll region. `min-h-full` on the inner column lets the
                editor's `flex-1` fill a short message to the bottom of the
                panel, the way the design draws it, without capping a long one. */}
            <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="flex flex-col min-h-full">{children}</div>
            </div>

            {/* Footer — the design's three-column grid: Discard, the sending
                identity centred, Send. The centre cell is its own column so the
                note stays centred on the panel rather than drifting with the
                width of whatever sits beside it. */}
            <div className="flex-shrink-0 grid grid-cols-[1fr_auto_1fr] items-center gap-3
                            px-5 py-3 border-t border-[#EDEBE6] bg-white">
                <div className="flex justify-start">
                    <button
                        type="button"
                        onClick={onDiscard}
                        data-testid={discardTestId}
                        className="flex items-center gap-2 h-9 px-[14px] rounded-[9px]
                                   text-[13.5px] font-medium text-[#A3382B]
                                   hover:bg-[#FBEEEC] transition-colors"
                    >
                        <Trash2 className="w-[15px] h-[15px]" />
                        Discard
                    </button>
                </div>

                <span
                    className="text-xs text-[#A29B95] text-center whitespace-nowrap"
                    data-testid={noteTestId}
                >
                    {note}
                </span>

                <div className="flex items-center justify-end gap-3">
                    {footerExtra}
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={blocked}
                        title={sendTitle}
                        data-testid={footerSendTestId}
                        className="flex items-center gap-2 h-9 px-[18px] rounded-[9px] bg-[#4D11F6]
                                   hover:bg-[#3F0BD1] disabled:opacity-60 text-white
                                   text-[13.5px] font-semibold transition-colors"
                    >
                        {busy
                            ? <Loader2 className="w-[15px] h-[15px] animate-spin" />
                            : <Send className="w-[15px] h-[15px]" />}
                        {sendLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
